import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Button, Spinner, Alert } from 'reactstrap';

const PreviewPanel = ({
  open,
  status,
  error,
  onClose,
  setStatus,
  setError,
  onLog,
  actions,
  projectId,
  version,
}) => {
  const iframeRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [pendingAction, setPendingAction] = useState('');
  const requestSeq = useRef(0);

  const runEnsure = useCallback(
    async ({ rebuild = false, reinstall = false } = {}) => {
      if (!projectId || !version || !actions || !actions.ensureWorkspacePreview) {
        onLog('Preview request failed: Preview is not configured (missing project or action).');
        setError('Preview is not configured (missing project or action).');
        setStatus('error');
        return;
      }

      const seq = ++requestSeq.current;
      const action = reinstall ? 'reinstall' : rebuild ? 'rebuild' : 'load';
      const actionLabel = action.charAt(0).toUpperCase() + action.slice(1);
      onLog(`${actionLabel} preview started.`);
      setError('');
      setPendingAction(action);
      setStatus(reinstall || rebuild ? 'building' : 'loading');

      try {
        const result = await actions.ensureWorkspacePreview({
          projectId,
          version,
          rebuild,
          reinstall,
        });
        console.log('result', result);

        if (seq !== requestSeq.current) return;

        if (!result || result.status === 'error') {
          onLog(`${actionLabel} preview failed: ${(result && result.error) || 'Preview failed'}`);
          setPreviewUrl('');
          setError((result && result.error) || 'Preview failed');
          setStatus('error');
          return;
        }

        const url = result.url || '';
        if (!url) {
          onLog(`${actionLabel} preview failed: Preview URL missing from server response`);
          setPreviewUrl('');
          setError('Preview URL missing from server response');
          setStatus('error');
          return;
        }

        const engine = (process.env.REACT_APP_BUILDER_ENGINE || '').replace(/\/$/, '');
        let finalUrl = `${engine}${url}`;
        if (rebuild || reinstall) {
          const sep = finalUrl.includes('?') ? '&' : '?';
          finalUrl = `${finalUrl}${sep}t=${Date.now()}`;
        }

        setPreviewUrl(finalUrl);
        setStatus('ready');
        if (result.steps && result.steps.install) {
          onLog('Preview dependencies installed successfully.');
        }
        if (result.steps && result.steps.build) {
          onLog('Preview build completed successfully.');
        }
        if (!(result.steps && (result.steps.install || result.steps.build))) {
          onLog(`${actionLabel} preview loaded successfully.`);
        }

        if (iframeRef.current) {
          iframeRef.current.src = finalUrl;
        }
      } catch (err) {
        if (seq !== requestSeq.current) return;
        onLog(`${actionLabel} preview failed: ${(err && err.message) || 'Preview failed'}`);
        setPreviewUrl('');
        setError((err && err.message) || 'Preview failed');
        setStatus('error');
      } finally {
        if (seq === requestSeq.current) setPendingAction('');
      }
    },
    [actions, onLog, projectId, version, setStatus, setError],
  );

  useEffect(() => {
    if (!open) return undefined;
    runEnsure({});
    return undefined;
  }, [open, projectId, version]);

  if (!open) return null;

  const isBusy = status === 'loading' || status === 'building';

  return (
    <aside className="vibe-preview-panel d-flex flex-column h-100 border-left">
      <header className="vibe-preview-header">
        <h2 className="vibe-preview-title">Preview</h2>
        <div className="vibe-preview-actions" role="group" aria-label="Preview actions">
          <button
            type="button"
            className="vibe-preview-action is-default"
            disabled={isBusy}
            title="Use existing dist, or build if missing"
            onClick={() => runEnsure({})}
          >
            {pendingAction === 'load' ? <Spinner size="sm" color="secondary" /> : null}
            Load
          </button>
          <button
            type="button"
            className="vibe-preview-action"
            disabled={isBusy}
            title="Run vite build, then load"
            onClick={() => runEnsure({ rebuild: true })}
          >
            {pendingAction === 'rebuild' ? <Spinner size="sm" color="secondary" /> : null}
            Rebuild
          </button>
          <button
            type="button"
            className="vibe-preview-action is-quiet"
            disabled={isBusy}
            title="npm install + vite build, then load"
            onClick={() => runEnsure({ reinstall: true })}
          >
            {pendingAction === 'reinstall' ? <Spinner size="sm" color="secondary" /> : null}
            Reinstall
          </button>
        </div>
        <button
          type="button"
          className="vibe-more-close"
          aria-label="Close"
          title="Close"
          onClick={onClose}
        >
          <i className="wb webconnect-times" aria-hidden="true" />
        </button>
      </header>

      {error ? (
        <Alert color="danger" className="mb-0 rounded-0 small">
          <div className="font-weight-bold mb-1">Preview error</div>
          <div className="text-break" style={{ whiteSpace: 'pre-wrap' }}>
            {error}
          </div>
          <Button
            color="danger"
            size="sm"
            outline
            className="mt-2"
            onClick={() => runEnsure({ rebuild: true })}
          >
            Retry rebuild
          </Button>
        </Alert>
      ) : null}

      <div className="vibe-preview-body position-relative overflow-hidden bg-white">
        {isBusy ? (
          <div className="d-flex align-items-center justify-content-center h-100 text-muted small">
            <Spinner size="sm" className="mr-2" />
            {status === 'loading' ? 'Preparing preview…' : 'Building preview…'}
          </div>
        ) : null}

        {!isBusy && !error && status === 'ready' && previewUrl ? (
          <iframe
            ref={iframeRef}
            title="Workspace preview"
            className="w-100 h-100 border-0"
            src={previewUrl}
            sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
          />
        ) : null}

        {!isBusy && !error && status !== 'ready' ? (
          <div className="d-flex align-items-center justify-content-center h-100 text-muted small p-3 text-center">
            Open preview to load the built app.
          </div>
        ) : null}
      </div>
    </aside>
  );
};

export default PreviewPanel;
